import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FilePen, Send, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { FileUpload } from '@/components/ui/FileUpload';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { formatDate } from '@/lib/format';
import { applyFieldErrors } from '@/lib/forms';
import { activitiesApi } from '../../api/activities.api';
import {
  ACTIVITY_TYPE_HINT,
  ACTIVITY_PRIORITY_HINT,
  ACTIVITY_PRIORITY_OPTIONS,
  ACTIVITY_TYPE_OPTIONS,
} from '../../activities/constants';
import {
  activityFormResolver,
  activitySchema,
  emptyActivityForm,
  type ActivityFormValues,
} from '../../activities/schema';
import type { ActivityPublication, ClassActivity } from '@/types/activity';
import type { TaughtClass } from '../../types/teacher';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

interface CreateActivityFormProps {
  currentClass: TaughtClass;
  /**
   * The activity being corrected, when the form is open to edit rather than to
   * set. The caller keys the form on it, so the fields start from its values.
   */
  editing?: ClassActivity | null;
  onSuccess: () => void;
  onCancel: () => void;
}

/** An activity as the form holds it — every field a string, like a blank one. */
function toFormValues(activity: ClassActivity): ActivityFormValues {
  return {
    classSectionUuid: activity.classSection.uuid,
    subjectUuid: activity.subject.uuid,
    title: activity.title,
    activityType: activity.activityType,
    priority: activity.priority,
    description: activity.description,
    instructions: activity.instructions ?? '',
    dueDate: activity.dueDate,
    maxPoints: activity.maxPoints === null ? '' : String(activity.maxPoints),
  };
}

export function CreateActivityForm({
  currentClass,
  editing = null,
  onSuccess,
  onCancel,
}: CreateActivityFormProps) {
  const [guideline, setGuideline] = useState<File | null>(null);
  /** The edit's stored sheet was taken off; sent with the save, not before. */
  const [removesSaved, setRemovesSaved] = useState(false);
  /** Which of the two submit buttons is saving, so only that one spins. */
  const [savingAs, setSavingAs] = useState<ActivityPublication | null>(null);
  const isDraft = editing?.publication === 'DRAFT';

  const form = useForm<ActivityFormValues>({
    resolver: activityFormResolver,
    defaultValues: editing
      ? toFormValues(editing)
      : emptyActivityForm(currentClass.classSectionUuid, currentClass.subjectUuid),
  });
  useLiveFormErrors(form);

  const {
    formState: { isSubmitting, errors },
    register,
    watch,
    reset,
    setError,
  } = form;

  const selectedType = watch('activityType');
  const selectedPriority = watch('priority');

  function handleCancel() {
    setGuideline(null);
    reset(emptyActivityForm(currentClass.classSectionUuid, currentClass.subjectUuid));
    onCancel();
  }

  const failure = editing ? 'Could not update this activity' : 'Could not set this activity';

  const onSubmit = form.handleSubmit(async (values, event) => {
    // Both buttons submit the form, so validation guards both; the one pressed
    // says whether this is a draft.
    const submitter =
      event?.nativeEvent instanceof SubmitEvent ? event.nativeEvent.submitter : null;
    const publication: ActivityPublication =
      submitter instanceof HTMLButtonElement && submitter.value === 'DRAFT' ? 'DRAFT' : 'PUBLISHED';
    const isPublishing = isDraft && publication === 'PUBLISHED';
    setSavingAs(publication);

    try {
      const parsed = activitySchema.parse(values);
      const saved = editing
        ? await activitiesApi.update(
            editing.uuid,
            {
              // The class and subject are not editable, and the server refuses
              // them, so the changes are listed rather than spread.
              title: parsed.title,
              activityType: parsed.activityType,
              priority: parsed.priority,
              description: parsed.description,
              // Sent even when emptied: '' is how an edit takes instructions off.
              instructions: values.instructions.trim(),
              dueDate: parsed.dueDate,
              maxPoints: parsed.maxPoints,
              // Only a draft being released says so; a draft saved again stays one.
              ...(isPublishing && { publication: 'PUBLISHED' as const }),
              // A new file replaces the old one on its own; removal is only for none.
              ...(removesSaved && !guideline && { removeGuideline: true as const }),
            },
            guideline,
          )
        : await activitiesApi.create({ ...parsed, publication }, guideline);
      toast.success(
        saved.publication === 'DRAFT'
          ? `${saved.title} is saved as a draft`
          : editing && !isPublishing
            ? `${saved.title} is updated`
            : `${saved.title} is set`,
        saved.publication === 'DRAFT'
          ? 'Only you can see it. Publish it when it is ready.'
          : `${saved.classSection.displayName} · due ${formatDate(saved.dueDate)}`,
      );
      setGuideline(null);
      reset(emptyActivityForm(currentClass.classSectionUuid, currentClass.subjectUuid));
      onSuccess();
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        applyFieldErrors(setError, caught);
        toast.error(failure, caught.message);
        return;
      }
      toast.error(failure, 'Something went wrong. Please try again.');
    } finally {
      setSavingAs(null);
    }
  });

  return (
    <form
      id="assign-activity-form"
      onSubmit={onSubmit}
      noValidate
      aria-label={editing ? 'Edit class activity' : 'Assign a class activity'}
      className="card flex flex-col gap-5 p-6 border-2 border-indigo-500/30 rounded-[18px] bg-surface shadow-md animate-in fade-in duration-200"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line pb-4">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-[12px] bg-indigo-500/15 text-indigo-400 border border-indigo-500/25">
            <Sparkles className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <h3 className="text-base font-bold text-ink">
              {editing ? 'Edit Class Activity' : 'Assign a Class Activity'}
            </h3>
            <p className="text-xs text-ink-3 font-medium mt-0.5">
              {isDraft
                ? 'A draft — only you can see it until you publish it.'
                : editing
                  ? 'Changes reach the class and their families as soon as you save.'
                  : 'A project, practical or presentation, set for one of your classes. Save it as a draft to finish it later.'}
            </p>
          </div>
        </div>
        <span className="px-3 py-1 rounded-pill text-xs font-bold bg-indigo-500/10 text-indigo-400 border border-indigo-500/25">
          {editing
            ? `${editing.classSection.displayName} · ${editing.subject.name}`
            : `${currentClass.className} · ${currentClass.subject}`}
        </span>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <Input
            label="Title"
            placeholder="e.g. 3D geometry working model"
            required
            error={errors.title?.message}
            {...register('title')}
          />
        </div>
        <Select
          label="Kind of activity"
          options={ACTIVITY_TYPE_OPTIONS}
          hint={selectedType ? ACTIVITY_TYPE_HINT[selectedType] : undefined}
          error={errors.activityType?.message}
          {...register('activityType')}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          label="How urgent"
          options={ACTIVITY_PRIORITY_OPTIONS}
          hint={
            selectedPriority
              ? ACTIVITY_PRIORITY_HINT[selectedPriority]
              : 'Families read this in their own panel.'
          }
          error={errors.priority?.message}
          {...register('priority')}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <DatePicker label="Due date" required error={errors.dueDate?.message} {...register('dueDate')} />
        <Input
          label="Marked out of (Optional)"
          placeholder="e.g. 20"
          inputMode="numeric"
          hint="Leave empty if this activity is not scored."
          error={errors.maxPoints?.message}
          {...register('maxPoints')}
        />
      </div>

      <Textarea
        label="What the children have to do"
        rows={2}
        placeholder="Build a 3D model showing cones, cylinders and spheres, with the surface-area formula for each."
        required
        error={errors.description?.message}
        {...register('description')}
      />

      <Textarea
        label="How to hand it in (optional)"
        rows={2}
        placeholder="A 2-page report with photographs of the model."
        error={errors.instructions?.message}
        {...register('instructions')}
      />

      <FileUpload
        label="Guidelines sheet (optional)"
        hint={
          editing?.guideline && removesSaved
            ? `${editing.guideline.name} will be removed when you save.`
            : editing?.guideline
              ? `Currently ${editing.guideline.name}. Choose a file only to replace it.`
              : 'A rubric, a diagram or a sample handed out with the activity (PDF, JPG, PNG up to 5 MB).'
        }
        file={guideline}
        current={
          editing?.guideline && !removesSaved
            ? {
                name: editing.guideline.name,
                size: editing.guideline.sizeBytes,
                path: activitiesApi.guidelinePath(editing.uuid),
              }
            : null
        }
        onRemoveCurrent={() => setRemovesSaved(true)}
        onSelect={setGuideline}
      />

      <div className="flex items-center justify-end gap-2.5 border-t border-line pt-4">
        <Button variant="ghost" type="button" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        {/* A draft can be saved again as a draft; a published activity cannot go back. */}
        {(!editing || isDraft) && (
          <Button
            type="submit"
            name="publication"
            value="DRAFT"
            variant="secondary"
            size="sm"
            leftIcon={<FilePen className="h-4 w-4" />}
            disabled={isSubmitting && savingAs !== 'DRAFT'}
            isLoading={savingAs === 'DRAFT'}
          >
            {editing ? 'Save Draft' : 'Save as Draft'}
          </Button>
        )}
        <Button
          type="submit"
          name="publication"
          value="PUBLISHED"
          variant="primary"
          size="sm"
          leftIcon={<Send className="h-4 w-4" />}
          disabled={isSubmitting && savingAs !== 'PUBLISHED'}
          isLoading={savingAs === 'PUBLISHED'}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
        >
          {!editing ? 'Assign Activity' : isDraft ? 'Publish' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}
